import React, { useState, useEffect, useMemo } from 'react'
import { designTokens } from '@/utils/design-tokens'
import { X } from 'lucide-react'
import { AcademyinfoService } from '@/api/generated/services/AcademyinfoService'
import type { TrendsResponse } from '@/api/generated/models/TrendsResponse'
import { formatIndicatorValue } from '../types'

interface YearDetailModalProps {
  isOpen: boolean
  onClose: () => void
  orgId: string
  indicatorMasterId: string
  indicatorName: string
  academyIds: string[]
  myAcademyId: string
  myAcademyRegion?: string
  availableYears: string[]
}

// Colors for graph blocks
const BLOCK_COLORS = {
  mySchool: designTokens.colors.primary.DEFAULT,
  compareAvg: designTokens.colors.secondary.DEFAULT,
  regionAvg: '#F59E0B',
  nationalAvg: '#8B5CF6',
}

const YearDetailModal: React.FC<YearDetailModalProps> = ({
  isOpen,
  onClose,
  orgId,
  indicatorMasterId,
  indicatorName,
  academyIds,
  myAcademyId,
  myAcademyRegion,
  availableYears,
}) => {
  const [trendsData, setTrendsData] = useState<TrendsResponse | null>(null)
  const [isLoading, setIsLoading] = useState(false)

  useEffect(() => {
    if (!isOpen || !indicatorMasterId || !orgId) return

    const fetchTrends = async () => {
      setIsLoading(true)
      try {
        const response = await AcademyinfoService.appsAcademyinfoViewsGetTrends({
          academyIds: academyIds.join(','),
          indicatorMasterId,
          orgId,
          years: availableYears.join(','),
          region: myAcademyRegion || undefined,
        })
        setTrendsData(response)
      } catch (err) {
        console.error('Failed to fetch trends:', err)
      } finally {
        setIsLoading(false)
      }
    }

    fetchTrends()
  }, [isOpen, indicatorMasterId, orgId, academyIds, availableYears, myAcademyRegion])

  const years = useMemo(() => [...(trendsData?.years || [])].sort(), [trendsData])
  const unit = trendsData?.indicator?.unit || ''
  const myAcademy = trendsData?.academies.find(a => a.id === myAcademyId)
  const compareAcademies = trendsData?.academies.filter(a => a.id !== myAcademyId) || []
  const averages = trendsData?.averages || []

  // Graph items: my school + averages
  const graphItems = useMemo(() => {
    const items: { label: string; color: string; values: Record<string, number | null> }[] = []
    if (myAcademy) {
      items.push({
        label: myAcademy.name,
        color: BLOCK_COLORS.mySchool,
        values: (myAcademy.values || {}) as Record<string, number | null>,
      })
    }
    const colorMap = [BLOCK_COLORS.compareAvg, BLOCK_COLORS.regionAvg, BLOCK_COLORS.nationalAvg]
    averages.forEach((avg, idx) => {
      items.push({
        label: avg.label,
        color: colorMap[idx] || designTokens.colors.gray[4],
        values: (avg.values || {}) as Record<string, number | null>,
      })
    })
    return items
  }, [myAcademy, averages])

  if (!isOpen) return null

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center">
      <div className="fixed inset-0 bg-black/50" onClick={onClose} />

      <div
        className="relative bg-white rounded-lg shadow-xl flex flex-col"
        style={{ width: '860px', maxWidth: '95vw', maxHeight: '85vh' }}
      >
        {/* Header */}
        <div
          className="flex items-center justify-between px-6 py-4 flex-shrink-0"
          style={{ borderBottom: `1px solid ${designTokens.colors.gray[1]}` }}
        >
          <div>
            <h2 style={{ fontFamily: designTokens.typography.fontFamily.sans[0], fontSize: '18px', fontWeight: 700, color: designTokens.colors.gray[6] }}>
              연도별 상세보기
            </h2>
            <div className="flex items-center gap-2 mt-1">
              <span style={{ fontFamily: designTokens.typography.fontFamily.sans[0], fontSize: '14px', color: designTokens.colors.primary.DEFAULT, fontWeight: 600 }}>
                {indicatorName}
              </span>
              {unit && (
                <span style={{ fontSize: '12px', color: designTokens.colors.gray[4] }}>(단위: {unit})</span>
              )}
            </div>
          </div>
          <button onClick={onClose} style={{ background: 'none', border: 'none', cursor: 'pointer', color: designTokens.colors.gray[4] }}>
            <X className="w-5 h-5" />
          </button>
        </div>

        {/* Content */}
        <div className="flex-1 overflow-auto px-6 py-4">
          {isLoading ? (
            <div className="flex items-center justify-center h-32">
              <span style={{ fontSize: '14px', color: designTokens.colors.gray[4] }}>데이터를 불러오는 중...</span>
            </div>
          ) : !trendsData ? (
            <div className="flex items-center justify-center h-32">
              <span style={{ fontSize: '14px', color: designTokens.colors.gray[4] }}>데이터가 없습니다.</span>
            </div>
          ) : (
            <>
              {/* 4 Mini Bar Charts */}
              <div className="flex gap-4 mb-6 overflow-x-auto pb-2">
                {(() => {
                  const globalMax = Math.max(
                    ...graphItems.flatMap(item => years.map(y => item.values[y] ?? 0)),
                    1
                  )
                  return graphItems.map(item => {
                  return (
                    <div
                      key={item.label}
                      className="flex-1 min-w-[160px] rounded border p-3"
                      style={{ borderColor: designTokens.colors.gray[1] }}
                    >
                      {/* Chart title */}
                      <div className="flex items-center gap-1.5 mb-3">
                        <div className="w-3 h-3 rounded-sm flex-shrink-0" style={{ backgroundColor: item.color }} />
                        <span style={{ fontFamily: designTokens.typography.fontFamily.sans[0], fontSize: '13px', fontWeight: 600, color: designTokens.colors.gray[6] }}>
                          {item.label}
                        </span>
                      </div>

                      {/* Bars */}
                      <div className="flex gap-2 justify-center">
                        {years.map(year => {
                          const value = item.values[year] ?? null
                          const BAR_MAX_HEIGHT = 80
                          const barPx = value !== null ? Math.max(Math.round((value / globalMax) * BAR_MAX_HEIGHT), 4) : 0

                          return (
                            <div key={year} className="flex flex-col items-center flex-1" style={{ minWidth: '32px' }}>
                              {/* Value label */}
                              <span style={{ fontSize: '11px', fontWeight: 600, color: designTokens.colors.gray[6], whiteSpace: 'nowrap', marginBottom: '4px' }}>
                                {value !== null ? (
                                  unit === '%' || unit === '배' || unit === '편' || unit === ':1'
                                    ? value.toFixed(1)
                                    : new Intl.NumberFormat('ko-KR').format(Math.round(value))
                                ) : '-'}
                              </span>
                              {/* Bar area */}
                              <div className="flex items-end" style={{ height: `${BAR_MAX_HEIGHT}px` }}>
                                <div
                                  className="rounded-t"
                                  style={{
                                    height: `${barPx}px`,
                                    width: '28px',
                                    backgroundColor: item.color,
                                    transition: 'height 0.3s ease',
                                  }}
                                />
                              </div>
                              {/* Year label */}
                              <span style={{ fontSize: '11px', color: designTokens.colors.gray[4], marginTop: '4px' }}>
                                {year}
                              </span>
                            </div>
                          )
                        })}
                      </div>
                    </div>
                  )
                })})()}
              </div>

              {/* Table: my school + comparison schools */}
              <div className="rounded border overflow-auto" style={{ borderColor: designTokens.colors.gray[1] }}>
                <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                  <thead>
                    <tr>
                      <th style={{ padding: '8px 12px', textAlign: 'left', fontFamily: designTokens.typography.fontFamily.sans[0], fontSize: '13px', fontWeight: 600, color: designTokens.colors.gray[6], backgroundColor: designTokens.colors.gray[0], borderBottom: `1px solid ${designTokens.colors.gray[1]}`, width: '160px' }}>
                        학교
                      </th>
                      {years.map(year => (
                        <th key={year} style={{ padding: '8px 12px', textAlign: 'right', fontFamily: designTokens.typography.fontFamily.sans[0], fontSize: '13px', fontWeight: 600, color: designTokens.colors.gray[6], backgroundColor: designTokens.colors.gray[0], borderBottom: `1px solid ${designTokens.colors.gray[1]}` }}>
                          {year}
                        </th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {/* My School */}
                    {myAcademy && (
                      <tr style={{ backgroundColor: designTokens.colors.secondary.light + '15' }}>
                        <td style={{ padding: '8px 12px', fontFamily: designTokens.typography.fontFamily.sans[0], fontSize: '14px', fontWeight: 600, color: designTokens.colors.primary.DEFAULT, borderBottom: `1px solid ${designTokens.colors.gray[1]}` }}>
                          {myAcademy.name}
                        </td>
                        {years.map(year => {
                          const value = myAcademy.values?.[year] ?? null
                          // Calculate rank: count how many comparison schools have higher value
                          const allValues = trendsData.academies
                            .map(a => a.values?.[year] ?? null)
                            .filter((v): v is number => v !== null)
                          const rank = value !== null
                            ? allValues.filter(v => v > value).length + 1
                            : null

                          return (
                            <td key={year} style={{ padding: '8px 12px', textAlign: 'right', fontFamily: designTokens.typography.fontFamily.sans[0], fontSize: '14px', fontWeight: 600, color: designTokens.colors.gray[6], borderBottom: `1px solid ${designTokens.colors.gray[1]}` }}>
                              {formatIndicatorValue(value, unit)}
                              {rank !== null && (
                                <span style={{ fontSize: '11px', color: designTokens.colors.secondary.dark, marginLeft: '4px' }}>
                                  ({rank}/{allValues.length})
                                </span>
                              )}
                            </td>
                          )
                        })}
                      </tr>
                    )}

                    {/* Comparison Schools */}
                    {compareAcademies.map(academy => (
                      <tr key={academy.id} className="hover:bg-gray-50">
                        <td style={{ padding: '8px 12px', fontFamily: designTokens.typography.fontFamily.sans[0], fontSize: '14px', color: designTokens.colors.gray[6], borderBottom: `1px solid ${designTokens.colors.gray[1]}` }}>
                          {academy.name}
                        </td>
                        {years.map(year => (
                          <td key={year} style={{ padding: '8px 12px', textAlign: 'right', fontFamily: designTokens.typography.fontFamily.sans[0], fontSize: '14px', color: designTokens.colors.gray[6], borderBottom: `1px solid ${designTokens.colors.gray[1]}` }}>
                            {formatIndicatorValue(academy.values?.[year] ?? null, unit)}
                          </td>
                        ))}
                      </tr>
                    ))}

                    {/* Averages */}
                    {averages.map((avg, idx) => (
                      <tr key={avg.label} style={{ backgroundColor: designTokens.colors.gray[0] }}>
                        <td style={{ padding: '8px 12px', fontFamily: designTokens.typography.fontFamily.sans[0], fontSize: '13px', fontWeight: 500, color: designTokens.colors.gray[5], borderBottom: `1px solid ${designTokens.colors.gray[1]}` }}>
                          <div className="flex items-center gap-1.5">
                            <div className="w-2.5 h-2.5 rounded-sm" style={{ backgroundColor: [BLOCK_COLORS.compareAvg, BLOCK_COLORS.regionAvg, BLOCK_COLORS.nationalAvg][idx] || designTokens.colors.gray[4] }} />
                            {avg.label}
                          </div>
                        </td>
                        {years.map(year => (
                          <td key={year} style={{ padding: '8px 12px', textAlign: 'right', fontFamily: designTokens.typography.fontFamily.sans[0], fontSize: '13px', color: designTokens.colors.gray[5], borderBottom: `1px solid ${designTokens.colors.gray[1]}` }}>
                            {formatIndicatorValue(avg.values?.[year] ?? null, unit)}
                          </td>
                        ))}
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </>
          )}
        </div>
      </div>
    </div>
  )
}

export default YearDetailModal
